import { ApiProperty, ApiPropertyOptional } from '@nestjs/swagger';
import { Expose } from 'class-transformer';
import { IsBoolean, IsInt, IsOptional, IsUUID, Min } from 'class-validator';

/**
 * Body of PATCH /job-phase/:id/contractor. Changes the contractor of the
 * phase; on a bump phase whose subdivision requires the bumpout timesheet the
 * change lands on the bump log row instead. The admin used to GET the bump
 * row only to learn its id and then PATCH one of two resources.
 */
export class JobPhaseContractorDto {
  @ApiProperty({ type: 'string', format: 'uuid' })
  @Expose()
  @IsUUID('4')
  contractorId!: string;
}

/**
 * Body of POST /job-phase/:id/assign-contractor (Regional Manager sheet).
 * Assigns the contractor and optionally the request delay in one write.
 * When the contractor already worked another phase of the same job the route
 * answers 409 with those phases unless `acknowledgePreviousAssignment` is
 * true; the admin used to GET every phase of the job to decide that.
 */
export class JobPhaseAssignContractorDto {
  @ApiProperty({ type: 'string', format: 'uuid' })
  @Expose()
  @IsUUID('4')
  contractorId!: string;

  @ApiPropertyOptional({ type: 'integer', minimum: 0 })
  @Expose()
  @IsOptional()
  @IsInt()
  @Min(0)
  requestDelayDays?: number;

  @ApiPropertyOptional({
    type: 'boolean',
    description:
      'Proceed even if this contractor was already assigned to another phase of the job.',
  })
  @Expose()
  @IsOptional()
  @IsBoolean()
  acknowledgePreviousAssignment?: boolean;
}

/** One phase of the job the contractor was already assigned to. */
export class JobPhasePreviousAssignmentDto {
  @ApiProperty({ type: 'string', format: 'uuid' })
  @Expose()
  id!: string;

  @ApiProperty({ type: 'string' })
  @Expose()
  phaseType!: string;

  @ApiProperty({ type: 'string' })
  @Expose()
  phaseName!: string;
}

/** 409 body of POST /job-phase/:id/assign-contractor. */
export class JobPhaseAssignConflictDto {
  @ApiProperty({ type: 'integer', example: 409 })
  @Expose()
  statusCode!: number;

  @ApiProperty({ type: 'string' })
  @Expose()
  message!: string;

  @ApiProperty({ type: () => [JobPhasePreviousAssignmentDto] })
  @Expose()
  previouslyAssignedPhases!: JobPhasePreviousAssignmentDto[];
}
